import {
    BillingState,
    FetchState,
    BillingPeriodWithEmployersResponse,
    BillingPeriodEmployer
} from "../types";
import * as BillingApi from "@/shared/api/billingService";
import * as storeHelper from "@/shared/storeHelper";
import { downloadCsvFile } from "@/shared/fileDownload";
import * as _ from "lodash";
import { defineStore } from "pinia";

export const initialState: BillingState = {
    monthlyBillingPeriodsFetchState: FetchState.Uninitialized,
    monthlyBillingPeriods: [],
    billingPeriodSelectedState: FetchState.Uninitialized,
    billingPeriodId: null,
    billingPeriodName: null,
    billingEmployerId: null,
    billingEmployerName: null,
    reconBillingPeriods: [],
    reconBillingPeriodsFetchState: FetchState.Uninitialized,
    reconPeriodSelectedState: FetchState.Uninitialized,
    reconPeriodId: null,
    reconPeriodName: null,
    reconEmployerId: null,
    reconEmployerName: null
};

const getEmployersFromBillingPeriods = (responses: BillingPeriodWithEmployersResponse[], selectedIndex: number) => {
    if (selectedIndex !== null &&
        selectedIndex !== undefined &&
        selectedIndex >= 0 &&
        selectedIndex < responses.length) {
        return responses[selectedIndex].employers;
    }

    const duplicateEmployers = responses.reduce(
        (flatArray, response, i) => flatArray.concat(response.employers), [] as BillingPeriodEmployer[]
    );

    return _.uniqBy(duplicateEmployers, er => er.employerId);
};

const getBillingPeriodWithEmployerId = (responses: BillingPeriodWithEmployersResponse[], employerId: null | number) => {
    if (employerId === null || employerId === undefined) return responses;

    return responses.filter(
        r => r.employers.findIndex(er => er.employerId === employerId) >= 0
    );
};

export const useBillingStore = defineStore("billing", {
    state: (): BillingState => (_.cloneDeep(initialState)),
    getters: {
        getMonthlyBillingPeriods: state => state.monthlyBillingPeriods,
        getSelectableBillingPeriods: state => getBillingPeriodWithEmployerId(state.monthlyBillingPeriods,
            state.billingEmployerId),
        getBillingEmployers: state => getEmployersFromBillingPeriods(state.monthlyBillingPeriods,
            state.billingPeriodId),
        getMonthlyBillingPeriodsLoaded: state => state.monthlyBillingPeriodsFetchState === FetchState.Loaded,
        getReportResultIsLoaded: state => state.billingPeriodSelectedState === FetchState.Loaded,
        getSelectedBillingPeriod: state => state.billingPeriodId,
        getSelectedBillingName: state => state.billingPeriodName,
        getReconBillingPeriods: state => state.reconBillingPeriods,
        getReconBillingPeriodsLoaded: state => state.reconBillingPeriodsFetchState === FetchState.Loaded,
        getReconPeriodIsLoaded: state => state.reconPeriodSelectedState === FetchState.Loaded,
        getSelectedReconPeriodId: state => state.reconPeriodId,
        getSelectedReconPeriodName: state => state.reconPeriodName,
        getSelectedReconEmployerId: state => state.reconEmployerId,
        getSelectedReconEmployerName: state => state.reconEmployerName,
        getReconEmployers: state => getEmployersFromBillingPeriods(state.reconBillingPeriods, state.reconPeriodId),
        getSelectableReconBillingPeriods: state => getBillingPeriodWithEmployerId(state.reconBillingPeriods,
            state.reconEmployerId)
    },
    actions: {
        resetBilling () {
            this.monthlyBillingPeriods = [];
            this.monthlyBillingPeriodsFetchState = FetchState.Uninitialized;
            this.billingPeriodId = null;
            this.billingPeriodName = null;
            this.billingEmployerId = null;
            this.billingEmployerName = null;
            this.billingPeriodSelectedState = FetchState.Uninitialized;
        },
        resetReconBilling () {
            this.reconBillingPeriods = [];
            this.reconBillingPeriodsFetchState = FetchState.Uninitialized;
            this.reconEmployerId = null;
            this.reconEmployerName = null;
            this.reconPeriodId = null;
            this.reconPeriodName = null;
            this.reconPeriodSelectedState = FetchState.Uninitialized;
        },
        setState (action: (s: BillingState) => void) {
            action(initialState);
        },
        setMonthlyBillingPeriods (billingPeriods: BillingPeriodWithEmployersResponse[]) {
            this.monthlyBillingPeriods = billingPeriods;
            this.monthlyBillingPeriodsFetchState = FetchState.Loaded;
        },
        setBillingPeriodId ({ billingPeriodId, reportName }) {
            this.billingPeriodId = billingPeriodId;
            this.billingPeriodName = reportName;
            this.billingPeriodSelectedState = FetchState.Loaded;
        },
        setBillingEmployer ({ employerId, employerName }) {
            this.billingEmployerId = employerId;
            this.billingEmployerName = employerName;
        },
        resetBillingPeriod () {
            this.billingPeriodId = null;
            this.billingPeriodName = null;
            this.billingPeriodSelectedState = FetchState.Uninitialized;
        },
        resetRecon () {
            this.reconBillingPeriods = [];
            this.reconBillingPeriodsFetchState = FetchState.Uninitialized;
            this.reconPeriodId = null;
            this.reconPeriodName = null;
            this.reconPeriodSelectedState = FetchState.Uninitialized;
        },
        setReconBillingPeriods (billingPeriods: BillingPeriodWithEmployersResponse[]) {
            this.reconBillingPeriods = billingPeriods;
            this.reconBillingPeriodsFetchState = FetchState.Loaded;
        },
        setReconPeriod ({ billingPeriodId, reportName }) {
            this.reconPeriodId = billingPeriodId;
            this.reconPeriodName = reportName;
            this.reconPeriodSelectedState = FetchState.Loaded;
        },
        setReconEmployer ({ employerId, employerName }) {
            this.reconEmployerId = employerId;
            this.reconEmployerName = employerName;
        },
        resetReconPeriod () {
            this.reconPeriodId = null;
            this.reconPeriodName = null;
            this.reconPeriodSelectedState = FetchState.Uninitialized;
        },
        async loadMonthlyBillingPeriods () {
            try {
                const result = await BillingApi.getMonthlyBillingPeriods();
                return this.setMonthlyBillingPeriods(result.data);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async loadMonthlyBillingPeriodsByPartner ({ partnerId }) {
            try {
                const result = await BillingApi.getMonthlyBillingPeriodsByPartner(partnerId);
                return this.setMonthlyBillingPeriods(result.data);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async loadMonthlyBillingPeriodsByEmployer ({ employerId }) {
            try {
                const result = await BillingApi.getMonthlyBillingPeriodsByEmployer(employerId);
                return this.setMonthlyBillingPeriods(result.data);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async loadReconBillingPeriodsByPartner ({ partnerId }) {
            try {
                const result = await BillingApi.getReconBillingPeriodsByPartner(partnerId);
                return this.setReconBillingPeriods(result.data);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async downloadReport ({ periodId, periodName }) {
            return BillingApi.downloadReport(periodId)
                .then(response => {
                    const fileName = "monthly-billing-report-" + periodName + ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async downloadEmployerReport ({ periodId, periodName, employerId }) {
            return BillingApi.downloadEmployerReport(periodId, employerId)
                .then(response => {
                    const fileName = `monthly-billing-report-${periodName}.csv`;
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async downloadPartnerReport ({ periodId, periodName, partnerId, employerId, employerName }) {
            return BillingApi.downloadPartnerReport(partnerId, periodId, employerId)
                .then(response => {
                    let fileName = `reconcilliation-report-${periodName}`;
                    if (employerName !== null && employerName !== undefined) {
                        fileName += `-${employerName}`;
                    }
                    fileName += ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async loadReconBillingPeriods () {
            try {
                const result = await BillingApi.getReconBillingPeriods();
                return this.setReconBillingPeriods(result.data);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async downloadReconReport ({ periodId, periodName }) {
            return BillingApi.downloadReconReport(periodId)
                .then(response => {
                    const fileName = "employee-paid-fees-reconciliation-report-" + periodName + ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async downloadSummaryReport ({ periodId, periodName }) {
            return BillingApi.downloadSummaryReport(periodId)
                .then(response => {
                    const fileName = "billing-summary-report-" + periodName + ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async downloadReconReportForPartner (
            { periodId, periodName, partnerId, employerId, employerName }
        ) {
            return BillingApi.downloadReconReportForPartner(
                    partnerId,
                    periodId,
                    employerId
                )
                .then(response => {
                    let fileName = `reconcilliation-report-${periodName}`;
                    if (employerName !== null && employerName !== undefined) {
                        fileName += `-${employerName}`;
                    }
                    fileName += ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});
